import type { ApiError } from './api-error';

/**
 * Field messages from a VALIDATION_FAILED or CONFLICT response, keyed by request field.
 * A nested path such as `roleIds.0` counts for its top-level field. These messages are
 * written for people (password policy, duplicate mobile), so forms show them as they are.
 */
export function serverFieldErrors(error: ApiError | null | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!error || !Array.isArray(error.details)) return result;
  for (const item of error.details as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const { field, message } = item as { field?: unknown; message?: unknown };
    if (typeof field !== 'string' || typeof message !== 'string') continue;
    const key = field.split('.')[0] ?? field;
    result[key] ??= message;
  }
  return result;
}

/**
 * A message for errors that no form field explains. Branches on the code; only a CONFLICT
 * shows the server's own message, since the API writes those for people ("This mandal is
 * used by villages", "The Client Admin role is not set up yet").
 */
export function describeApiError(error: ApiError, fallback: string): string {
  switch (error.code) {
    case 'CONFLICT':
      return error.message || fallback;
    case 'NETWORK_ERROR':
    case 'TIMEOUT':
      return 'Unable to reach the server. Check your connection and try again.';
    case 'FORBIDDEN':
      return 'You do not have permission to do this.';
    case 'NOT_FOUND':
      return 'This record no longer exists. Refresh the list and try again.';
    case 'RATE_LIMITED':
      return 'Too many attempts. Please wait a minute and try again.';
    case 'SERVICE_UNAVAILABLE':
      return 'The service is temporarily unavailable. Please try again shortly.';
    default:
      return fallback;
  }
}

export interface FormFailure<F extends string> {
  /** Server messages for this form's fields. */
  fields: Partial<Record<F, string>>;
  /** Shown above the buttons when no field explains the error. */
  general?: { message: string; requestId?: string };
}

/** Splits a failed submit into messages for the form's own fields and one for the rest. */
export function formFailure<F extends string>(
  error: ApiError,
  fields: readonly F[],
  fallback: string,
): FormFailure<F> {
  const server = serverFieldErrors(error);
  const result: FormFailure<F> = { fields: {} };
  for (const field of fields) {
    const message = server[field];
    if (message) result.fields[field] = message;
  }
  if (Object.keys(result.fields).length === 0) {
    result.general = { message: describeApiError(error, fallback), requestId: error.requestId };
  }
  return result;
}
